import type { FailureCause, GamePhase, RunState, ScoreBreakdown, StageDefinition, StageResult } from './types';

export interface UIElements {
  canvas: HTMLCanvasElement;
  loading: HTMLElement;
  title: HTMLElement;
  stageIntro: HTMLElement;
  overlay: HTMLElement;
  result: HTMLElement;
  help: HTMLDialogElement;
  live: HTMLElement;
  score: HTMLElement;
  combo: HTMLElement;
  pulses: HTMLElement;
  timer: HTMLElement;
  stage: HTMLElement;
  status: HTMLElement;
  pause: HTMLButtonElement;
  mute: HTMLButtonElement;
  motion: HTMLButtonElement;
  helpButton: HTMLButtonElement;
}

const shell = `
  <section class="game-shell" aria-label="Kinetic Bloom game">
    <div class="brand-mark" aria-label="SurpriseBliss presents">
      <span class="brand-mark__seed" aria-hidden="true"></span>
      <span>SurpriseBliss / Field Study 01</span>
    </div>
    <div class="playfield" id="playfield">
      <canvas id="game-canvas" tabindex="0" aria-label="The Kinetic Bloom garden playfield"></canvas>
      <div class="loading-screen is-visible" id="loading-screen" aria-live="polite">
        <div class="germination" aria-hidden="true"><i></i><i></i><i></i></div>
        <p class="eyebrow">Waking the physics garden</p>
        <p class="loading-status">Germinating collision shapes<span class="loading-dots">...</span></p>
      </div>
      <section class="title-screen" id="title-screen" hidden>
        <div class="title-copy">
          <p class="eyebrow">A five-garden physics ritual</p>
          <h1>Kinetic<br><em>Bloom</em></h1>
          <p class="dek">Pull a wayward seed through impossible flora. Spend two pulses to bend its fate. Land softly.</p>
          <button class="primary-action" id="start-button" type="button"><span>Enter the garden</span><b aria-hidden="true">Space</b></button>
        </div>
        <div class="title-specimen" aria-hidden="true">
          <span class="orbit orbit--one"></span><span class="orbit orbit--two"></span>
          <span class="specimen-seed"></span>
          <span class="specimen-label">No. 01 / Pull to launch</span>
        </div>
      </section>
      <section class="stage-intro" id="stage-intro" hidden aria-live="polite"></section>
      <section class="modal-layer" id="modal-layer" hidden></section>
      <section class="result-layer" id="result-layer" hidden></section>
      <div class="gesture-hint" id="gesture-hint" hidden>
        <span class="gesture-line" aria-hidden="true"></span>
        <p>Pull away from the seed. Release to fly.</p>
      </div>
    </div>
    <aside class="hud" aria-label="Run status">
      <div class="hud__stage"><span>Garden</span><strong id="hud-stage">01 / 05</strong></div>
      <div class="hud__score"><span>Score</span><strong id="hud-score">000000</strong></div>
      <div class="hud__metric"><span>Chain</span><strong id="hud-combo">—</strong></div>
      <div class="hud__metric"><span>Pulses</span><strong id="hud-pulses" aria-label="2 pulses remaining">● ●</strong></div>
      <div class="hud__metric"><span>Time</span><strong id="hud-timer">00:00.0</strong></div>
      <p class="hud__status" id="hud-status">Germinating</p>
      <nav class="hud__actions" aria-label="Game controls">
        <button id="pause-button" type="button" aria-label="Pause game" disabled>Pause</button>
        <button id="mute-button" type="button" aria-label="Mute sound" aria-pressed="false">Sound on</button>
        <button id="motion-button" type="button" aria-label="Use reduced motion" aria-pressed="false">Motion full</button>
        <button id="help-button" type="button" aria-label="Open controls">Controls</button>
      </nav>
    </aside>
  </section>
  <div class="sr-only" id="live-region" role="status" aria-live="polite" aria-atomic="true"></div>
  <dialog id="help-dialog" class="help-dialog" aria-labelledby="help-title">
    <button class="dialog-close" id="help-close" type="button" aria-label="Close controls">Close</button>
    <p class="eyebrow">Field notes</p>
    <h2 id="help-title">How to move a seed</h2>
    <div class="control-grid">
      <div><span>Pointer / touch</span><p>Pull from the seed and release. Tap during flight to pulse toward your touch.</p></div>
      <div><span>Keyboard</span><p>Arrow keys or WASD adjust aim. Space launches or pulses. P pauses. R retries.</p></div>
    </div>
    <p class="help-note">Collect the marked petals, avoid thorns, and enter the flower slowly. You have two pulses per attempt.</p>
  </dialog>`;

export class GameUI {
  readonly elements: UIElements;

  constructor(root: HTMLElement) {
    root.innerHTML = shell;
    const byId = <T extends HTMLElement>(id: string): T => {
      const element = document.getElementById(id);
      if (!element) throw new Error(`Missing UI element: ${id}`);
      return element as T;
    };
    this.elements = {
      canvas: byId<HTMLCanvasElement>('game-canvas'), loading: byId('loading-screen'), title: byId('title-screen'),
      stageIntro: byId('stage-intro'), overlay: byId('modal-layer'), result: byId('result-layer'),
      help: byId<HTMLDialogElement>('help-dialog'), live: byId('live-region'), score: byId('hud-score'),
      combo: byId('hud-combo'), pulses: byId('hud-pulses'), timer: byId('hud-timer'), stage: byId('hud-stage'),
      status: byId('hud-status'), pause: byId<HTMLButtonElement>('pause-button'), mute: byId<HTMLButtonElement>('mute-button'),
      motion: byId<HTMLButtonElement>('motion-button'), helpButton: byId<HTMLButtonElement>('help-button'),
    };
  }

  showTitle(returning: boolean): void {
    this.elements.loading.classList.remove('is-visible');
    window.setTimeout(() => { this.elements.loading.hidden = true; }, 500);
    this.elements.title.hidden = false;
    const dek = this.elements.title.querySelector('.dek');
    if (dek && returning) dek.textContent = 'The garden remembers your best run. Pull clean. Chain petals. Land softly.';
    requestAnimationFrame(() => this.elements.title.classList.add('is-visible'));
    this.setStatus('Garden ready');
  }

  hideTitle(): void { this.elements.title.classList.remove('is-visible'); this.elements.title.hidden = true; }

  showStageIntro(stage: StageDefinition, brief = false, onComplete?: () => void): void {
    this.elements.stageIntro.innerHTML = `<div><p class="eyebrow">Garden ${String(stage.number).padStart(2, '0')} / 05</p><h2>${stage.name}</h2><p>${stage.epitaph}</p>${brief ? '' : `<span>${stage.mechanic}</span>`}</div>`;
    this.elements.stageIntro.hidden = false;
    requestAnimationFrame(() => this.elements.stageIntro.classList.add('is-visible'));
    window.setTimeout(() => this.elements.stageIntro.classList.remove('is-visible'), 1500);
    window.setTimeout(() => { this.elements.stageIntro.hidden = true; onComplete?.(); }, 1900);
  }

  updateHUD(run: RunState, stage: StageDefinition): void {
    this.elements.score.textContent = Math.round(run.score + run.stageScore).toString().padStart(6, '0');
    this.elements.combo.textContent = run.combo > 1 ? `×${run.combo}` : '—';
    this.elements.pulses.textContent = `${'● '.repeat(run.pulses)}${'○ '.repeat(2 - run.pulses)}`.trim();
    this.elements.pulses.setAttribute('aria-label', `${run.pulses} ${run.pulses === 1 ? 'pulse' : 'pulses'} remaining`);
    const minutes = Math.floor(run.elapsed / 60).toString().padStart(2, '0');
    const seconds = (run.elapsed % 60).toFixed(1).padStart(4, '0');
    this.elements.timer.textContent = `${minutes}:${seconds}`;
    this.elements.stage.textContent = `${String(stage.number).padStart(2, '0')} / 05`;
    this.elements.pause.disabled = run.phase !== 'playing' && run.phase !== 'paused';
  }

  setPhase(phase: GamePhase): void {
    document.body.dataset.phase = phase;
    this.elements.pause.textContent = phase === 'paused' ? 'Resume' : 'Pause';
    this.elements.pause.setAttribute('aria-label', phase === 'paused' ? 'Resume game' : 'Pause game');
  }

  setStatus(message: string, announce = false): void {
    this.elements.status.textContent = message;
    if (announce) {
      this.elements.live.textContent = '';
      requestAnimationFrame(() => { this.elements.live.textContent = message; });
    }
  }

  showPause(): void {
    this.elements.overlay.innerHTML = `<div class="pause-panel"><p class="eyebrow">Time held still</p><h2>Garden paused</h2><p>Your seed is exactly where you left it.</p><button class="primary-action" id="resume-button" type="button"><span>Return to flight</span><b aria-hidden="true">P</b></button></div>`;
    this.elements.overlay.hidden = false;
  }

  showFailure(cause: FailureCause): void {
    const copy: Record<FailureCause, [string, string]> = {
      thorn: ['Caught by the briars', 'The thorn garden keeps what rushes through it.'],
      'out-of-bounds': ['Lost beyond the paper', 'The seed slipped outside this garden’s edge.'],
      stalled: ['The seed fell quiet', 'No momentum remains, but the path is still yours.'],
    };
    this.elements.overlay.innerHTML = `<div class="failure-panel"><p class="eyebrow">Attempt interrupted</p><h2>${copy[cause][0]}</h2><p>${copy[cause][1]}</p><button class="primary-action" id="retry-button" type="button"><span>Try the line again</span><b aria-hidden="true">R</b></button></div>`;
    this.elements.overlay.hidden = false;
  }

  hideOverlay(): void { this.elements.overlay.hidden = true; this.elements.overlay.innerHTML = ''; }

  showStageResult(stage: StageDefinition, result: StageResult): void {
    this.elements.result.innerHTML = resultMarkup(`${stage.name} opened`, result.breakdown, result.score, result.isBest ? 'New garden best' : 'Stage complete', 'Continue onward', 'continue-button');
    this.elements.result.hidden = false;
  }

  showCampaignResult(results: StageResult[], total: number, rank: string, isBest: boolean): void {
    const rows = results.map((result, index) => `<li><span>${String(index + 1).padStart(2, '0')} / ${result.stageId.replaceAll('-', ' ')}</span><strong>${result.score.toLocaleString()}</strong></li>`).join('');
    this.elements.result.innerHTML = `<div class="campaign-result"><div class="rank-seal"><span>Botanical rank</span><strong>${rank}</strong></div><div class="result-copy"><p class="eyebrow">All five gardens opened</p><h2>The seed remembers the way.</h2><p class="result-total">${total.toLocaleString()} <span>total score</span></p><p class="best-note">${isBest ? 'A new campaign best. Press it into the field notes.' : 'The route is alive. A cleaner line will climb the ranks.'}</p><ol>${rows}</ol><button class="primary-action" id="replay-button" type="button"><span>Begin another run</span><b aria-hidden="true">Space</b></button></div></div>`;
    this.elements.result.hidden = false;
  }

  hideResult(): void { this.elements.result.hidden = true; this.elements.result.innerHTML = ''; }
}

function resultMarkup(title: string, score: ScoreBreakdown, total: number, note: string, action: string, id: string): string {
  return `<div class="stage-result"><div class="result-heading"><p class="eyebrow">${note}</p><h2>${title}</h2><strong>${total.toLocaleString()}</strong></div><dl><div><dt>Petal line</dt><dd>+${score.petals}</dd></div><div><dt>Chain</dt><dd>+${score.chain}</dd></div><div><dt>Tempo</dt><dd>+${score.time}</dd></div><div><dt>Held pulses</dt><dd>+${score.pulses}</dd></div><div><dt>Soft landing</dt><dd>+${score.landing}</dd></div><div class="penalty"><dt>Retries</dt><dd>−${score.retryPenalty}</dd></div></dl><button class="primary-action" id="${id}" type="button"><span>${action}</span><b aria-hidden="true">Space</b></button></div>`;
}
